import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/credits";
import { Loader2, Database, Download, HardDriveDownload, FileSpreadsheet, FileText } from "lucide-react";

const DATASETS = [
  { key: "installers", label: "Installers" },
  { key: "certificates", label: "Certificates" },
  { key: "activations", label: "Bottle Activations" },
  { key: "credit-ledger", label: "Credit Ledger" },
  { key: "follow-ups", label: "Follow-Ups" },
  { key: "audit-log", label: "Audit Log" },
];

function humanSize(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
}

export default function Backups() {
  const [data, setData] = useState({ history: [], last_backup: null });
  const [loading, setLoading] = useState(true);
  const [backing, setBacking] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/backups"); setData(data); }
    catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runBackup = async () => {
    setBacking(true);
    try { await api.post("/admin/backups"); toast.success("Backup completed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Backup failed"); }
    finally { setBacking(false); }
  };

  const exportData = async (dataset, format) => {
    setDownloading(`${dataset}-${format}`);
    try {
      const res = await api.get(`/admin/export`, { params: { dataset, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${dataset}.${format === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${dataset}`);
    } catch (e) { toast.error("Export failed"); }
    finally { setDownloading(null); }
  };

  const downloadBackup = async (id) => {
    setDownloading(id);
    try {
      const res = await api.get(`/admin/backups/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `sgbinoxe-backup.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
    finally { setDownloading(null); }
  };

  return (
    <DashboardLayout title="Data & Backups">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">System</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Data exports & backups</h2>
        <p className="mt-1 text-sm text-neutral-500">Export platform data or create a full database snapshot.</p>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="exports-panel">
        <h3 className="mb-5 font-display text-base font-medium text-white">Data Exports</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {DATASETS.map((d) => (
            <div key={d.key} className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-[#0A0A0A] px-4 py-3" data-testid={`export-${d.key}`}>
              <span className="text-sm text-neutral-200">{d.label}</span>
              <div className="flex gap-2">
                <button onClick={() => exportData(d.key, "csv")} disabled={downloading === `${d.key}-csv`} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-2.5 py-1.5 text-xs text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60" data-testid={`export-${d.key}-csv`}>{downloading === `${d.key}-csv` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} CSV</button>
                <button onClick={() => exportData(d.key, "xlsx")} disabled={downloading === `${d.key}-xlsx`} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-2.5 py-1.5 text-xs text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60" data-testid={`export-${d.key}-xlsx`}>{downloading === `${d.key}-xlsx` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Excel</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="backups-panel">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Database className="h-5 w-5 text-[#FF1C2A]" />
            <div>
              <h3 className="font-display text-base font-medium text-white">Database Backups</h3>
              <p className="text-xs text-neutral-500">Last backup: {data.last_backup ? fmtDate(data.last_backup, true) : "Never"}{data.last_status ? ` · ${data.last_status}` : ""}</p>
            </div>
          </div>
          <button onClick={runBackup} disabled={backing} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="run-backup-btn">{backing ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />} Create Backup</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : data.history.length === 0 ? (
          <p className="py-10 text-center text-sm text-neutral-500">No backups yet. Create your first snapshot above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500"><th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Status</th><th className="py-3 pr-3">Collections</th><th className="py-3 pr-3">Documents</th><th className="py-3 pr-3">Size</th><th className="py-3 text-right">Download</th></tr></thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.id} className="border-b border-white/[0.04]" data-testid="backup-row">
                    <td className="py-3 pr-3 text-neutral-300">{fmtDate(h.created_at, true)}</td>
                    <td className="py-3 pr-3"><span className={h.status === "completed" ? "text-emerald-400" : "text-[#FF6069]"}>{h.status}</span></td>
                    <td className="py-3 pr-3 text-neutral-400">{h.collections}</td>
                    <td className="py-3 pr-3 text-neutral-400">{h.documents}</td>
                    <td className="py-3 pr-3 text-neutral-400">{humanSize(h.size_bytes)}</td>
                    <td className="py-3 text-right"><button onClick={() => downloadBackup(h.id)} disabled={downloading === h.id} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-2.5 py-1.5 text-xs text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60" data-testid={`download-backup-${h.id}`}>{downloading === h.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} JSON</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
